import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { shortDate } from '../lib/format';

/**
 * Public certificate verification.
 *
 * Every certificate this platform issues prints "scan the QR code or visit
 * the link below to verify this certificate" above an address ending
 * `/verify/<number>`. There was no page at that address: the route fell
 * through to the catch-all and every QR code on every certificate landed a
 * prospective employer on the course catalogue.
 *
 * Open to anyone, by design and by necessity — the person checking a
 * credential is the one party guaranteed not to have an account here. The
 * endpoint behind it returns only what a verifier needs: the name, the
 * course, the dates and whether it still stands. Not an email, not a score,
 * not anything else the holder did not put on the certificate itself.
 */

interface Verification {
  found: boolean;
  status: 'VALID' | 'REVOKED' | 'EXPIRED' | 'NOT_FOUND' | string;
  message: string;
  certificateNo?: string;
  learnerName?: string;
  title?: string;
  issuedAt?: string;
  completionDate?: string;
}

export function VerifyCertificate() {
  const { certificateNo } = useParams<{ certificateNo: string }>();
  const navigate = useNavigate();

  const [typed, setTyped] = useState(certificateNo ?? '');
  const [result, setResult] = useState<Verification | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (number: string) => {
    setChecking(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api<Verification>(`/verify/${encodeURIComponent(number)}`));
    } catch (caught) {
      const failure = caught as ApiError;
      // The endpoint is deliberately rate-limited, because it is the one
      // someone would use to go looking for valid numbers.
      setError(
        failure.status === 429
          ? 'Too many checks from this address. Please wait a minute and try again.'
          : (failure.message ?? 'The certificate could not be checked. Please try again.'),
      );
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (certificateNo) void check(certificateNo);
  }, [certificateNo, check]);

  const valid = result?.found && result.status === 'VALID';

  return (
    <div className="verify">
      <section className="verify__head">
        <p className="verify__eyebrow">SkillXzen</p>
        <h1>Verify a certificate</h1>
        <p className="muted">
          Enter the certificate number printed on the document, or scan its QR code.
        </p>
      </section>

      <form
        className="verify__form"
        onSubmit={(event) => {
          event.preventDefault();
          const number = typed.trim().toUpperCase();
          if (!number) return;
          // Through the address bar, so the result can be linked and shared —
          // which is the whole point of a verification page.
          navigate(`/verify/${encodeURIComponent(number)}`);
        }}
      >
        <label className="field">
          <span className="field__label">Certificate number</span>
          <input
            className="field__input"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="SX-0000-0000-0000"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <button className="btn" type="submit" disabled={checking || !typed.trim()}>
          {checking ? 'Checking…' : 'Verify'}
        </button>
      </form>

      {error ? <div className="alert alert--error">{error}</div> : null}

      {result ? (
        <section
          className={`verify__result verify__result--${valid ? 'valid' : result.found ? 'warn' : 'none'}`}
          aria-live="polite"
        >
          <p className="verify__verdict">
            <span className="verify__mark" aria-hidden="true">
              {valid ? '✓' : result.found ? '!' : '✕'}
            </span>
            {result.message}
          </p>

          {result.found ? (
            <dl className="verify__facts">
              <div>
                <dt>Awarded to</dt>
                <dd className="verify__name">{result.learnerName}</dd>
              </div>
              <div>
                <dt>Course</dt>
                <dd>{result.title}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>{result.completionDate ? shortDate(result.completionDate) : '—'}</dd>
              </div>
              <div>
                <dt>Issued</dt>
                <dd>{result.issuedAt ? shortDate(result.issuedAt) : '—'}</dd>
              </div>
              <div>
                <dt>Number</dt>
                <dd className="verify__no">{result.certificateNo}</dd>
              </div>
            </dl>
          ) : null}
        </section>
      ) : null}

      <p className="verify__note muted">
        This page confirms only what is printed on the certificate. It shows no other
        information about the holder.
      </p>
    </div>
  );
}
